/* ------------------------------------------------------------------
   KV: make collection filters respect stock.

   THE PROBLEM
   Shopify's `filter.v.option.size` matches any product that HAS that size,
   sold out or not. Only `filter.v.availability=1` narrows it — and when both
   are applied Shopify requires the SAME variant to satisfy both, which is
   exactly what we want: "a 4X that is actually in stock".

   Measured on /collections/all-swim-collection before this fix
   (shown / actually buyable): XS 57/42, S 59/54, M 57/36, L 59/43,
   XL 57/36, 2X 57/30, 4X 57/29, 6X 32/18. The gap widens at the extended
   end of the range — a 4X shopper saw 57 products and could buy 29 — which
   is the opposite of what an XS–6X brand should do.

   WHY JAVASCRIPT AND NOT LIQUID
   assets/facets.js builds the filter URL with `new FormData(form)` →
   `URLSearchParams` (see createFilteredSearchParams, which only
   special-cases the price range). So a hidden input inside the facet form
   is picked up automatically. Filtering is already JS-driven through
   <facet-filters-form>, so there is no window where a customer can filter
   but this has not run. Doing it here keeps 51KB of facet Liquid untouched.

   WHAT THIS DOES
     1. puts a hidden filter.v.availability=1 into every facet form
     2. disables and hides the theme's own Availability facet, so it cannot
        contribute a conflicting value or be switched off by a customer
     3. hides the "Availability: In stock" chip in the active-filter row —
        without this the customer can simply remove the filter again
     4. keeps availability on the "Clear all" link
     5. on a direct load that already carries a size filter but no
        availability (an old email or ad link), rewrites the URL once so the
        guarantee holds on first paint too

   TELLING A CHIP FROM "CLEAR ALL"
   Both are wrapped in <facet-remove>, so the wrapper cannot distinguish
   them and an earlier version of this file hid "Clear all" by mistake. A
   value chip carries a visually-hidden facet label ("Availability:",
   "Size:") and the class active-facets__button--light; "Clear all" carries
   neither. That label is the discriminator used below.

   Re-runs after every filter change, because facets.js replaces the facet
   and active-filter markup wholesale on each render.

   DELIBERATE LIMIT: an UNFILTERED collection view is untouched, so sold-out
   sizes are still visible when browsing normally. That matters — clicking a
   sold-out size is what surfaces the Klaviyo back-in-stock form, and that
   click is the demand signal behind the reorder decisions.
   ------------------------------------------------------------------ */
(function () {
  "use strict";

  if (window.__kvFilterInStock) return;
  window.__kvFilterInStock = true;

  var PARAM = "filter.v.availability";
  var VALUE = "1";
  var MARK = "data-kv-instock";
  var OPTION_PREFIX = "filter.v.option.";

  function hasFacets() {
    return !!document.querySelector("facet-filters-form");
  }

  function paramsOf(href) {
    var i = href.indexOf("?");
    return new URLSearchParams(i === -1 ? "" : href.slice(i + 1));
  }

  /* 1. hidden input in every facet form ---------------------------- */
  function ensureHiddenInput() {
    var forms = document.querySelectorAll("facet-filters-form form");
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var id = form.getAttribute("id") || "";
      if (id.indexOf("Sort") !== -1) continue;
      if (form.querySelector("input[" + MARK + "]")) continue;
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = PARAM;
      input.value = VALUE;
      input.setAttribute(MARK, "");
      form.appendChild(input);
    }
  }

  /* 2. neutralise the theme's own Availability facet ---------------- */
  function hideAvailabilityFacet() {
    var own = document.querySelectorAll(
      'input[name="' + PARAM + '"]:not([' + MARK + "])"
    );
    for (var i = 0; i < own.length; i++) {
      var el = own[i];
      el.disabled = true;
      var group =
        el.closest("details") ||
        el.closest(".js-filter") ||
        el.closest(".facets__disclosure-vertical");
      if (group) group.style.display = "none";
    }
  }

  /* 3 + 4. active-filter chips -------------------------------------- */
  function fixActiveChips() {
    if (!new URLSearchParams(window.location.search).has(PARAM)) return;

    var anchors = document.querySelectorAll("[class*='active-facets'] a");
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      var href = a.getAttribute("href") || "";
      var params = paramsOf(href);

      var isValueChip =
        !!a.querySelector(".visually-hidden") ||
        a.className.indexOf("active-facets__button--light") !== -1;

      if (isValueChip) {
        /* every chip EXCEPT the availability one keeps availability in its
           href, so an href that has dropped it is the availability chip */
        if (!params.has(PARAM)) {
          var chip = a.closest("facet-remove") || a;
          chip.style.display = "none";
        }
      } else if (!params.has(PARAM)) {
        /* "Clear all" — clear the filters but keep in-stock */
        params.set(PARAM, VALUE);
        a.setAttribute("href", href.split("?")[0] + "?" + params.toString());
      }
    }
  }

  /* 5. direct loads that filter by size but not by stock ------------ */
  function fixDirectLoad() {
    var here = new URLSearchParams(window.location.search);
    if (here.has(PARAM)) return;

    var filtersByOption = false;
    here.forEach(function (_value, key) {
      if (key.indexOf(OPTION_PREFIX) === 0) filtersByOption = true;
    });
    if (!filtersByOption) return;

    here.set(PARAM, VALUE);
    window.location.replace(
      window.location.pathname + "?" + here.toString() + window.location.hash
    );
  }

  function apply() {
    if (!hasFacets()) return;
    ensureHiddenInput();
    hideAvailabilityFacet();
    fixActiveChips();
  }

  function boot() {
    if (!hasFacets()) return;
    fixDirectLoad();
    apply();

    var timer;
    try {
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(apply, 60);
      }).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}

    window.addEventListener("popstate", function () {
      setTimeout(apply, 60);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
